// EpicEngage V1 Types

export type IndustryType = 'FNB' | 'RETAIL' | 'HEALTHCARE' | 'WELLNESS';
export type RegionType = 'SG' | 'US' | 'GLOBAL';
export type CustomerStatus = 'NEW' | 'ACTIVE' | 'INACTIVE';
export type EventType = 'CUSTOMER_CREATED' | 'ORDER_PLACED' | 'REVIEW_SUBMITTED' | 'VOUCHER_REDEEMED' | 'TIME_INACTIVE_30D';
export type TemplateType = 'AUTOMATION' | 'EVENT' | 'MANUAL';
export type EditorType = 'STRUCTURED' | 'GRAPESJS';
export type TemplateOrigin = 'SYSTEM_GENERATED' | 'EVENT_LIBRARY' | 'MANUAL';
export type SequenceKey = 'NEW_CUSTOMER_ONBOARDING' | 'POST_ACTION_FOLLOWUP' | 'INACTIVE_30D';
export type MessageType = 'AUTOMATED' | 'EVENT_SCHEDULED' | 'MANUAL_BLAST';
export type ChannelType = 'EMAIL';
export type MessageStatus = 'SCHEDULED' | 'SENT' | 'FAILED' | 'SKIPPED';
export type ToneType = 'FRIENDLY' | 'PROFESSIONAL' | 'PREMIUM';

export interface Merchant {
  id: string;
  user_id: string;
  name: string;
  industry: IndustryType;
  region: RegionType;
  onboarding_completed: boolean;
  created_at: string;
}

export interface Customer {
  id: string;
  merchant_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  last_activity_at: string | null;
  status_tag: CustomerStatus;
}

export interface CustomerEvent {
  id: string;
  merchant_id: string;
  customer_id: string | null;
  type: EventType;
  source: string | null;
  metadata: Record<string, any> | null;
  occurred_at: string;
}

export interface EmailTemplateV2 {
  id: string;
  merchant_id: string;
  name: string;
  type: TemplateType;
  editor_type: EditorType;
  origin: TemplateOrigin;
  subject: string;
  body_json: any;
  grapesjs_json: any;
  html_cache: string | null;
  created_at: string;
  updated_at: string;
}

export interface Sequence {
  id: string;
  merchant_id: string;
  key: SequenceKey;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface SequenceStep {
  id: string;
  sequence_id: string;
  step_order: number;
  delay_hours: number;
  template_id: string | null;
  guard_once_per_customer: boolean;
  template?: EmailTemplateV2;
}

export interface ScheduledMessage {
  id: string;
  merchant_id: string;
  customer_id: string | null;
  type: MessageType;
  channel: ChannelType;
  template_id: string | null;
  subject_snapshot: string | null;
  html_snapshot: string | null;
  scheduled_at: string;
  sent_at: string | null;
  status: MessageStatus;
  provider_message_id: string | null;
  error: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
}

export interface EventLibrary {
  id: string;
  name: string;
  region: RegionType;
  event_date: string;
  active_year: number;
  description: string | null;
}

export interface EventLibraryTemplate {
  id: string;
  event_library_id: string;
  industry: IndustryType;
  step_order: number;
  send_offset_days: number;
  subject: string;
  body_seed: string;
  default_cta_label: string | null;
  default_cta_url_hint: string | null;
}

export interface EnabledEvent {
  id: string;
  merchant_id: string;
  event_library_id: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  event?: EventLibrary;
}

export interface MerchantSettings {
  merchant_id: string;
  default_sender_email: string | null;
  tone: ToneType;
  weekly_send_cap: number;
  quiet_hours_start: number;
  quiet_hours_end: number;
  discount_ceiling_percent: number;
  created_at: string;
  updated_at: string;
}

export interface ManualBlast {
  id: string;
  merchant_id: string;
  template_id: string;
  audience: 'ALL' | 'NEW' | 'ACTIVE' | 'INACTIVE';
  scheduled_at: string | null;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  status: MessageStatus;
  created_at: string;
  template?: EmailTemplateV2;
}

// Timeline item for customer detail view
export interface TimelineItem {
  id: string;
  type: 'event' | 'message';
  occurred_at: string;
  data: CustomerEvent | ScheduledMessage;
}

// Sequence display info
export const SEQUENCE_INFO: Record<SequenceKey, { name: string; description: string; steps: number }> = {
  NEW_CUSTOMER_ONBOARDING: {
    name: 'New Customer Onboarding',
    description: 'Welcome new customers with a 3-email sequence',
    steps: 3
  },
  POST_ACTION_FOLLOWUP: {
    name: 'Post-Action Follow-up',
    description: 'Thank customers after orders, reviews, or redemptions',
    steps: 1
  },
  INACTIVE_30D: {
    name: 'Re-engagement (30 days)',
    description: 'Win back inactive customers after 30 days',
    steps: 1
  }
};

export const AUDIENCE_OPTIONS = [
  { value: 'ALL', label: 'All Customers', description: 'Send to everyone' },
  { value: 'NEW', label: 'New Customers', description: 'Joined in the last 30 days' },
  { value: 'ACTIVE', label: 'Active Customers', description: 'Activity in the last 30 days' },
  { value: 'INACTIVE', label: 'Inactive Customers', description: 'No activity for 30+ days' }
] as const;
